import React from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';
import { useSelector } from 'react-redux';

import SettingsTile from 'components/common/tile/SettingsTile';
import AuthTile from 'components/common/tile/AuthTile';
import FocusGroup from 'components/focus/FocusGroup';
import { Providers } from 'app-types';
import applicationActions from 'common/support/applicationActions';

import focusManager from 'support/focus/FocusManager';

import textToSpeechManager from 'support/tts/TextToSpeechManager';
import { VideoCtvRedux } from 'store-types';
import styles from './SettingsRow.module.scss';

const messages = {
  legalNotices: {
    id: 'tile.settings.legalNotices',
    description: 'legal notices',
  },
  closedCaptioning: {
    id: 'tile.settings.closedCaptioning.header',
    description: 'closed captioning',
  },
  closedCaptioningOn: {
    id: 'tile.settings.closedCaptioning.on',
    description: 'closed captioning state on',
  },
  closedCaptioningOff: {
    id: 'tile.settings.closedCaptioning.off',
    description: 'closed captioning state off',
  },
  ttsLegalNotices: {
    id: 'tile.settings.tts.legalNotices',
    description: 'tts for legal notices',
  },
  ttsClosedCaptioningOn: {
    id: 'tile.settings.tts.closedCaptioning.on',
    description: 'tts for turning closed captioning on',
  },
  ttsClosedCaptioningOff: {
    id: 'tile.settings.tts.closedCaptioning.off',
    description: 'tts for turning closed captioning off',
  },
  contactUs: {
    id: 'tile.settings.contactUs',
    description: 'contact us',
  },
  ttsEndOfNavigation: {
    id: 'navigation.tts.ttsEndOfNavigation',
    description: 'TTS text for navigation command cannot be executed',
  },
};

interface SettingsRowProps {
  className?: string | null;
  focusIdPrefix?: string | null;
}

const SettingsRow: React.FC<SettingsRowProps> = ({
  className = null,
  focusIdPrefix = '',
}) => {
  const intl = useIntl();

  const usePPlus = useSelector(
    (state: VideoCtvRedux) =>
      state.kitchenSink?.data?.config?.modules?.Auth?.params?.shortCode_PPlus,
  );

  const useMVPD = useSelector(
    (state: VideoCtvRedux) =>
      state.kitchenSink?.data?.config?.modules?.Auth?.params?.shortCode_MVPD,
  );

  const legalNoticesEnabled = useSelector(
    (state: VideoCtvRedux) => state.kitchenSink.data.settings?.legal?.enabled,
  );

  const legalNoticesData = useSelector(
    (state: VideoCtvRedux) => state.kitchenSink.data.settings?.legal?.label,
  );

  const legalNoticesLabel =
    legalNoticesData || intl.formatMessage(messages.legalNotices);

  const isCcOn = useSelector((state: VideoCtvRedux) => state.closedCaptioning);

  const onCcClick = () => {
    history.push({
      pathname: '/closedCaptions',
    });
  };

  const history = useHistory() as MemoryHistory;

  const onLegalNoticesClick = () => {
    history.push({
      pathname: '/legal',
    });
  };

  const onContactUsClick = () => {
    history.push({
      pathname: '/contactUs',
    });
  };

  const ttsEndOfNavigation = useIntl().formatMessage(
    messages.ttsEndOfNavigation,
  );

  const onEndOfNavigation = () => {
    textToSpeechManager.add(ttsEndOfNavigation);
    textToSpeechManager.speak();
    if (!(focusManager?.currentFocusId as string)?.includes('contact-us')) {
      focusManager.setCurrentFocus('left-nav-focusable-content');
    }
  };

  return (
    <FocusGroup
      focusId="row-settings-data"
      horizontal
      className={classNames(
        'h-100 d-flex justify-content-center flex-column',
        className,
      )}
      data-testid="row-settings"
      onEndOfNavigation={onEndOfNavigation}
      onActionOverride={(action: string) => {
        if (action === applicationActions.back) {
          focusManager.setCurrentFocus('left-nav-focusable-content');
          return true;
        }
        return false;
      }}
    >
      <div className={classNames('d-flex justify-content-flex-start flex-row')}>
        {usePPlus === '1' && useMVPD === '1' && (
          <AuthTile
            id={`${focusIdPrefix}row-settings-auth-paramountplus`}
            className={styles.tile}
            provider={Providers.PARAMOUNT_PLUS}
          />
        )}
        {usePPlus === '1' && useMVPD === '1' && (
          <AuthTile
            id={`${focusIdPrefix}row-settings-auth-adobe`}
            className={styles.tile}
            provider={Providers.ADOBE}
          />
        )}
        {legalNoticesEnabled && (
          <SettingsTile
            id={`${focusIdPrefix}row-settings-data-legal-notices`}
            className={styles.tile}
            tts={legalNoticesLabel}
            onClick={onLegalNoticesClick}
          >
            <span
              data-testid="legal-header"
              className={classNames(
                'text-uppercase text-wrap',
                styles.legalHeader,
              )}
            >
              {legalNoticesLabel}
            </span>
          </SettingsTile>
        )}
        <SettingsTile
          id={`${focusIdPrefix}row-settings-data-closed-captioning`}
          className={styles.tile}
          onClick={onCcClick}
          tts={intl.formatMessage(
            messages[
              isCcOn ? 'ttsClosedCaptioningOn' : 'ttsClosedCaptioningOff'
            ],
          )}
        >
          <div className="d-flex align-items-center justify-content-center">
            <div
              data-testid="closed-captioning-icon"
              className={classNames(
                'd-flex align-items-center justify-content-center',
                'mr-3',
                'text-uppercase',
                styles.ccIcon,
                isCcOn && styles.ccOn,
              )}
            >
              {intl.formatMessage(messages.closedCaptioning)}
            </div>
            <div
              data-testid="closed-captioning-status"
              className="text-uppercase"
            >
              {intl.formatMessage(
                messages[isCcOn ? 'closedCaptioningOn' : 'closedCaptioningOff'],
              )}
            </div>
          </div>
        </SettingsTile>
        <SettingsTile
          id={`${focusIdPrefix}row-settings-data-contact-us`}
          className={styles.tile}
          tts={intl.formatMessage(messages.contactUs)}
          onClick={onContactUsClick}
        >
          <span className="text-uppercase">
            {intl.formatMessage(messages.contactUs)}
          </span>
        </SettingsTile>
      </div>
    </FocusGroup>
  );
};

export default React.memo(SettingsRow);
